import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { applyTeam } from "@/lib/spaces";
import { fireConfetti, konamiListener, playSound } from "@/lib/fun";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);

  const loadMembers = useCallback(() => api.get("/team").then((r) => { applyTeam(r.data.members); setMembers(r.data.members); }), []);
  const load = useCallback(() => Promise.all([
    api.get("/settings/me").then((r) => setPrefs(r.data)),
    api.get("/settings/team").then((r) => setTeam(r.data)),
    loadMembers(),
  ]).catch(() => { setPrefs((p) => p || {}); setTeam((t) => t || null); }), [loadMembers]);

  useEffect(() => {
    if (user) load();
    else { setPrefs(null); setTeam(null); }
  }, [user, load]);

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.theme = prefs?.theme || "midnight";
    el.classList.toggle("reduce-motion", !!prefs?.reduce_motion);
  }, [prefs?.theme, prefs?.reduce_motion]);

  useEffect(() => {
    if (!prefs?.konami) return undefined;
    return konamiListener(() => {
      document.documentElement.classList.add("eight-bit");
      playSound("arcade");
      toast("8-bit mode unlocked for 30 seconds");
      setTimeout(() => document.documentElement.classList.remove("eight-bit"), 30000);
    });
  }, [prefs?.konami]);

  const savePrefs = async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    try {
      const r = await api.put("/settings/me", { value: patch });
      setPrefs(r.data);
      if ("color" in patch || "emoji" in patch) await loadMembers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveTeam = async (patch, msg) => {
    try {
      const r = await api.put("/settings/team", { value: patch });
      setTeam(r.data);
      if (msg) toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const celebrate = (title) => {
    if (!prefs) return;
    if (prefs.confetti && !prefs.reduce_motion) fireConfetti(prefs.confetti_style);
    if (prefs.sounds) playSound(prefs.sound_pack, "done");
    if (prefs.compliments && team?.ai?.enabled?.compliments) {
      api.post("/ai/compliment", { title }).then((r) => r.data.text && toast(r.data.text)).catch(() => {});
    }
  };

  const ready = !user || (prefs && team !== undefined && (team || prefs));
  const value = { prefs: prefs || {}, team, members, savePrefs, saveTeam, celebrate, reloadMembers: loadMembers, reload: load };
  return <Ctx.Provider value={value}>{ready ? children : <div className="min-h-screen" data-testid="settings-loading" />}</Ctx.Provider>;
}

export const useSettings = () => useContext(Ctx);
export const aiOn = (team, feature) => team?.ai?.enabled?.[feature] !== false;
